import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Home, Plus, MapPin, BookOpen, Loader2, CalendarDays, Download, Search } from "lucide-react";
import { houseCoverUrl, mapsUrl } from "@/lib/constants";
import { toast } from "sonner";

import { tx, lz } from "@/i18n";
const empty = { name: "", address: "", city: "", description: "", image_url: "", welcome_book_url: "", wifi_name: "", wifi_password: "", owner_ids: [], bedrooms: 0, notes: "" };
const DEFAULT_SHEET = "https://docs.google.com/spreadsheets/d/1n89RHUHXeLFYwPtouDi2ms00wgtLUyKDVIBL5sZ7iXM/export?format=csv";

const STATUS_CLS = (s) => s === "Bezet" ? "bg-red-100 text-red-700" : s === "Schoonmaak" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700";

export default function Houses() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [houses, setHouses] = useState(null);
  const [owners, setOwners] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [csvUrl, setCsvUrl] = useState(DEFAULT_SHEET);
  const [importing, setImporting] = useState(false);
  const [search, setSearch] = useState("");
  const canManage = ["admin"].includes(user.role);

  const load = () => api.get("/houses").then(({ data }) => setHouses(data)).catch((err) => { setHouses([]); toast.error(apiError(err)); });
  useEffect(() => {
    load();
    if (canManage) api.get("/users", { params: { role: "client" } }).then(({ data }) => setOwners(data)).catch(() => {});
  }, []);

  const doImport = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/houses/import", { csv_url: csvUrl, auto_link: true });
      toast.success(tx("{0} toegevoegd, {1} bijgewerkt, {2} gekoppeld aan agenda", { 0: data.created, 1: data.updated, 2: data.linked }));
      setImportOpen(false);
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setImporting(false); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const q = search.trim().toLowerCase();
  const filteredHouses = !houses ? houses : (q ? houses.filter((h) => [h.name, h.address, h.city].filter(Boolean).join(" ").toLowerCase().includes(q)) : houses);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, owner_ids: form.owner_ids || [], bedrooms: Number(form.bedrooms) || 0 };
      await api.post("/houses", payload);
      toast.success(tx("Woning toegevoegd"));
      setOpen(false);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="animate-fade-up">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display font-bold text-3xl tracking-tight">{tx("Woningen")}</h1>
          <p className="text-muted-foreground mt-1">{tx("Beheer uw woningen en welkomstboeken.")}</p>
        </div>
        {canManage && (
          <div className="flex items-center gap-2">
          <Dialog open={importOpen} onOpenChange={setImportOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2 border-secondary text-secondary hover:bg-secondary/10" data-testid="import-houses-btn"><Download className="h-4 w-4" /> {tx("Importeer uit Sheet")}</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display">{tx("Woningen importeren")}</DialogTitle></DialogHeader>
              <div className="space-y-2">
                <Label>{tx("Google Sheet CSV-link")}</Label>
                <Input value={csvUrl} onChange={(e) => setCsvUrl(e.target.value)} data-testid="import-csv-input" />
                <p className="text-xs text-muted-foreground">{tx("De sheet moet kolommen bevatten: Naam_Woning, Adres, Eigenaar_Email, Status. Woningen worden automatisch gekoppeld aan je Google-agenda op naam.")}</p>
              </div>
              <DialogFooter>
                <Button onClick={doImport} disabled={importing || !csvUrl} className="bg-primary hover:bg-primary/90" data-testid="run-import-btn">
                  {importing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {tx("Importeren")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-primary/90 gap-2" data-testid="add-house-btn"><Plus className="h-4 w-4" /> {tx("Woning")}</Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display">{tx("Nieuwe woning")}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>{tx("Naam *")}</Label><Input value={form.name} onChange={set("name")} className="mt-1" data-testid="house-name-input" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>{tx("Adres")}</Label><Input value={form.address} onChange={set("address")} className="mt-1" /></div>
                  <div><Label>{tx("Plaats")}</Label><Input value={form.city} onChange={set("city")} className="mt-1" /></div>
                </div>
                <div><Label>{tx("Afbeelding URL")}</Label><Input value={form.image_url} onChange={set("image_url")} className="mt-1" placeholder="https://..." /></div>
                <div><Label>{tx("Welkomstboek URL")}</Label><Input value={form.welcome_book_url} onChange={set("welcome_book_url")} className="mt-1" placeholder="https://..." data-testid="house-welcomebook-input" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>{tx("WiFi naam")}</Label><Input value={form.wifi_name} onChange={set("wifi_name")} className="mt-1" /></div>
                  <div><Label>{tx("WiFi wachtwoord")}</Label><Input value={form.wifi_password} onChange={set("wifi_password")} className="mt-1" /></div>
                </div>
                <div>
                  <Label>{tx("Eigenaren")}</Label>
                  <div className="mt-1 space-y-1.5 max-h-40 overflow-y-auto rounded-md border border-border p-2" data-testid="house-owners-list">
                    {owners.length === 0 && <p className="text-xs text-muted-foreground">{tx("Nog geen gebruikers met rol Eigenaar.")}</p>}
                    {owners.map((o) => (
                      <label key={o.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`owner-opt-${o.id}`}>
                        <input
                          type="checkbox"
                          checked={form.owner_ids.includes(o.id)}
                          onChange={() => setForm((f) => ({ ...f, owner_ids: f.owner_ids.includes(o.id) ? f.owner_ids.filter((x) => x !== o.id) : [...f.owner_ids, o.id] }))}
                          className="h-4 w-4 rounded border-input"
                        />
                        <span>{o.name} <span className="text-muted-foreground">({o.email})</span></span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{tx("Je kunt meerdere eigenaren koppelen. Elke eigenaar ziet alleen deze woning(en).")}</p>
                </div>
                <div><Label>{tx("Beschrijving")}</Label><Textarea value={form.description} onChange={set("description")} className="mt-1" /></div>
              </div>
              <DialogFooter>
                <Button onClick={save} disabled={saving || !form.name} className="bg-primary hover:bg-primary/90" data-testid="save-house-btn">
                  {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {tx("Opslaan")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      {houses && houses.length > 0 && (
        <div className="relative mb-6 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={tx("Zoek op naam, adres of plaats…")} className="pl-9" data-testid="houses-search" />
        </div>
      )}

      {!houses ? (
        <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
      ) : houses.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center text-muted-foreground">
          <Home className="h-10 w-10 mx-auto mb-3 opacity-40" />
          {tx("Nog geen woningen.")}
        </div>
      ) : filteredHouses.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center text-muted-foreground" data-testid="houses-no-results">
          <Search className="h-10 w-10 mx-auto mb-3 opacity-40" /> {tx("Geen woningen gevonden voor \"")}{search}".
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredHouses.map((h) => (
            <div key={h.id} onClick={() => navigate(`/app/houses/${h.id}`)} className="bg-card rounded-xl border border-border overflow-hidden shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-200 cursor-pointer" data-testid={`house-card-${h.id}`}>
              <div className="aspect-[16/10] bg-muted overflow-hidden">
                <img src={houseCoverUrl(h, "https://images.unsplash.com/photo-1580587771525-78b9dba3b914?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", 800)} alt={h.name} loading="lazy" decoding="async" className="w-full h-full object-cover" data-testid={`house-thumb-${h.id}`} />
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-display font-semibold text-lg">{h.name}</h3>
                  {user.role !== "guest" && h.computed_status && (
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full shrink-0 ${STATUS_CLS(h.computed_status)}`} data-testid={`house-status-badge-${h.id}`}>{h.computed_status}</span>
                  )}
                </div>
                {(h.address_label || h.address_url || h.city || h.address) && (
                  <a
                    href={h.address_url || mapsUrl([h.address, h.city].filter(Boolean).join(", "))}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="flex items-center gap-1.5 text-sm text-muted-foreground mt-1 hover:text-primary hover:underline w-fit"
                    data-testid={`house-address-link-${h.id}`}
                  >
                    <MapPin className="h-3.5 w-3.5 shrink-0" /> {h.address_label || [h.address, h.city].filter(Boolean).join(", ")}
                  </a>
                )}
                {((h.welcome_books && h.welcome_books.length > 0) || h.welcome_book_url) && (
                  <div className="flex items-center gap-1.5 text-sm text-secondary mt-3 font-medium">
                    <BookOpen className="h-4 w-4" /> {tx("Welkomstboek")}{h.welcome_books && h.welcome_books.length > 1 ? tx(" ({0} talen)", { 0: h.welcome_books.length }) : ""}
                  </div>
                )}
                {h.google_calendar_id && (
                  <div className="flex items-center gap-1.5 text-sm text-secondary mt-2 font-medium">
                    <CalendarDays className="h-4 w-4" /> {tx("Agenda gekoppeld")}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
